import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import { Button, Modal, Stack } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AppContext } from '../../../appContext/AppContext';
import AutocompleteObjectV2 from '../../../component/AutocompleteObjectV2';
import ButtonInPhieu from '../../../component/button-in-phieu';
import DatePickerCustom from '../../../component/date-picker/DatePickerCustom';
import LabelRequired from '../../../component/LabelRequired';
import TextField from '../../../component/TextField';
import { getCategory } from '../../../utils/AppFunction';
import {
	APP_TIMESTAMP_FORMAT,
	CODE,
	KEY_DS_DANH_MUC_TIEP_DON,
	RESPONSE_MESSAGE,
	TERM_CLASS_IDS,
} from '../../../utils/Constant';
import { formatDateTime, getErrorMessage } from '../../../utils/FormatUtils';
import { getIndexedDBItem } from '../../../utils/IndexedDB';
import WfButton from '../../../workflows/components/WfButton';
import { WfConstTrackerClinical } from '../../../workflows/Constant';
import {
	printGiayHenKham,
	saveDatLichHen,
	updateDatLichHen,
} from '../../services/DatLichHenService';
import InfoPatientRight from '../InfoPatientRight';
import { getAppointmentTimeOptions } from './constants';
import './style.scss';

type Props = {
	open: boolean;
	patientInfo: any;
	itemEdit: any;
	onClose: () => void;
	onSave?: (date: any) => void;
};

type FormValue = {
	id?: string;
	date: string | null;
	time: string | null;
	schedule?: string | null;
	service?: any;
	department?: any;
	reason?: string;
};

const DatLichHenKhamModal = ({ open, onClose, patientInfo, itemEdit, onSave }: Props) => {
	const { setIsLoading } = useContext(AppContext);
	const [formValues, setFormValues] = useState<FormValue>({
		date: new Date().toJSON(),
		time: null,
	});
	const [timeOptions, setTimeOptions] = useState<{ label: string; value: string }[]>([]);
	const [categories, setCategories] = useState({
		services: [],
		departments: [],
	});

	useEffect(() => {
		setTimeOptions(getAppointmentTimeOptions());
		getCategories();
		initFormValues(itemEdit);
	}, []);

	const initFormValues = (itemEdit: any) => {
		if (itemEdit?.id) {
			setFormValues({
				...itemEdit,
				schedule: itemEdit.scheduledDate,
				date: itemEdit.scheduledDate,
				time: getDateTimeValue(itemEdit.scheduledDate)?.time,
				service: {
					id: itemEdit.termId,
					code: itemEdit.termCode,
					name: itemEdit.termName,
				},
				department: {
					id: itemEdit.departmentId,
					code: itemEdit.departmentCode,
					name: itemEdit.departmentName,
				},
			});
			return;
		}

		setFormValues({
			...formValues,
			service: patientInfo?.visit?.termSvcCode
				? {
						id: patientInfo?.visit?.termSvcId,
						code: patientInfo?.visit?.termSvcCode,
						name: patientInfo?.visit?.termSvcName,
				  }
				: null,
			department: patientInfo?.visit?.deptCode
				? {
						id: patientInfo?.visit?.deptId,
						code: patientInfo?.visit?.deptCode,
						name: patientInfo?.visit?.deptName,
				  }
				: null,
			reason: patientInfo?.encounterDiagnosis?.reasonForEncounter,
		});
	};

	const getCategories = async () => {
		try {
			const [services, indexDBValues] = await Promise.all([
				getCategory(TERM_CLASS_IDS.KHAM_BENH),
				getIndexedDBItem(KEY_DS_DANH_MUC_TIEP_DON),
			]);
			setCategories({
				services,
				departments: indexDBValues.listPhongKham,
			});
		} catch (error) {
			console.error(error);
		}
	};

	const changeValue = (name: keyof FormValue, fieldValue: any) => {
		setFormValues({
			...formValues,
			[name]: fieldValue,
		});
	};

	const onDatLichHen = async () => {
		try {
			if (!isValidForm()) return;

			setIsLoading(true);
			const params = {
				patientId: patientInfo?.patientId,
				lastVisitId: patientInfo?.visitId,
				departmentId: formValues.department?.id,
				departmentCode: formValues.department?.code,
				departmentName: formValues.department?.name,
				termId: formValues.service?.id,
				termCode: formValues.service?.code,
				termName: formValues.service?.name,
				reason: formValues.reason ?? '',
				scheduledDate: getScheduleDate(formValues.date, formValues.time),
			};
			const isEditAppointment = formValues?.id;

			const response = isEditAppointment
				? await updateDatLichHen(formValues.id!, params)
				: await saveDatLichHen(params);

			const { code, data } = response ?? {};

			if (CODE.SUCCESS !== code) {
				const errorMessage = getErrorMessage(response);
				toast.error(errorMessage || RESPONSE_MESSAGE.ERROR);
				return;
			}

			setFormValues({
				...formValues,
				id: data.id,
			});
			onSave?.(data);
			toast.success(`${isEditAppointment ? 'Cập nhật' : 'Đặt'} lịch hẹn khám thành công`);
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const getScheduleDate = (dateValue: string | null, timeValue: string | null) => {
		const dateOnly = getDateTimeValue(dateValue)?.date;
		return formatDateTime(new Date(`${dateOnly}T${timeValue || '00:00:00'}`), APP_TIMESTAMP_FORMAT) ?? '';
	};

	const isValidForm = () => {
		let isValid = true;

		if (!formValues.service) {
			toast.warn('Chưa chọn dịch vụ khám');
			isValid = false;
			return;
		}

		if (!formValues.department) {
			toast.warn('Chưa chọn phòng khám');
			isValid = false;
			return;
		}

		return isValid;
	};

	const getDateTimeValue = (value: string | null) => {
		if (!value) return;

		const [date, time] = value.split('T');
		return {
			date,
			time,
		};
	};

	return (
		<Modal size="lg" show={open} animation centered onHide={onClose} scrollable>
			<Modal.Header closeButton>
				<Modal.Title>Đặt lịch hẹn khám lại</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div>
					<InfoPatientRight benhNhanInfo={patientInfo} />
				</div>

				<div className="separator" />

				<div className="p-2 d-flex gap-4 align-items-center">
					<div className="p-2 flex-1">
						<DatePickerCustom
							name="date"
							value={formValues.date}
							setDateValue={(date) => {
								changeValue('date', date);
							}}
							mode="date"
							inline
							className="appointment-date-picker"
							minDate={new Date()}
						/>
					</div>
					<div className="p-2 flex-1">
						<div className="appointment-time-picker">
							{!!timeOptions.length &&
								timeOptions.map((option) => (
									<button
										className={clsx('btn btn-light bg-hover-secondary', {
											'btn-info': formValues.time === option.value,
										})}
										key={option.value}
										onClick={() => {
											changeValue('time', option.value);
										}}
									>
										{option.label}
									</button>
								))}
						</div>
					</div>
				</div>

				<div className="separator" />

				<div className="my-2 w-50">
					<DatePickerCustom
						label="Thời gian hẹn khám:"
						labelClassName="spaces min-w-150"
						name="date"
						value={getScheduleDate(formValues.date, formValues.time)}
						mode="date-time"
						minDate={new Date()}
						onChange={(date: any) => {
							if (date) {
								const value = getDateTimeValue(
									formatDateTime(new Date(date), APP_TIMESTAMP_FORMAT) ?? null
								);
								setFormValues({
									...formValues,
									date: new Date(value!.date).toISOString(),
									time: value?.time ?? '',
								});
							}
						}}
					/>
				</div>

				<div className="d-flex gap-3 mt-2">
					<div className="flex-1">
						<LabelRequired className="min-w-90px" label="Dịch vụ" isRequired />
						<AutocompleteObjectV2
							options={categories.services}
							value={formValues?.service}
							name="service"
							onChange={(selectedOption) => {
								changeValue('service', selectedOption);
							}}
							getOptionLabel={(option) =>
								option.code ? `${option.code} - ${option.name}` : option.name
							}
						/>
					</div>
					<div className="flex-1">
						<LabelRequired label="Phòng khám" isRequired />
						<AutocompleteObjectV2
							options={categories.departments}
							value={formValues.department}
							name="dept"
							onChange={(selectedOption) => {
								changeValue('department', selectedOption);
							}}
						/>
					</div>
				</div>

				<div className="mt-2">
					<LabelRequired label="Lý do khám" />
					<TextField
						as="textarea"
						name="reason"
						value={formValues?.reason}
						onChange={(e: any) => changeValue('reason', e.target.value)}
						rows={2}
						className="resize-none"
					/>
				</div>
			</Modal.Body>

			<Modal.Footer>
				<Stack direction="horizontal" className="w-100 justify-content-between">
					<Stack direction="horizontal" className="gap-2">
						{formValues?.id && (
							<ButtonInPhieu
								fetchExport={printGiayHenKham}
								params={{
									fileType: 'pdf',
									id: formValues?.id,
								}}
								label="In giấy hẹn khám"
								className="btn-fill spaces px-16"
							/>
						)}
					</Stack>
					<Stack direction="horizontal" className="gap-2">
						<Button className="btn-danger spaces px-16" onClick={onClose}>
							Đóng
						</Button>
						{formValues?.id && (
							<WfButton
								tracker={WfConstTrackerClinical.APPOINTMENT}
								objectId={formValues.id}
								additionalFunc={() => {
									toast.success('Hủy đặt lịch hẹn thành công');
									onSave?.(null);
									setFormValues({
										...formValues,
										date: new Date().toJSON(),
										time: null,
										id: undefined,
									});
								}}
							/>
						)}
						<Button className="btn-fill spaces px-16" onClick={onDatLichHen}>
							Lưu
						</Button>
					</Stack>
				</Stack>
			</Modal.Footer>
		</Modal>
	);
};

export default DatLichHenKhamModal;
